// MoveFileDialog — ONE smart verb, "Where to?", on `FileDestinationPicker`
// (the destination as decision tiles, the site and the asset by search on the
// selection family, the folder in the destination's vocabulary); one line says
// what will happen before the button. The rule (ADR-068 §1/§2): bytes never
// move, the key and the born-on facts never change — a move re-points the
// typed LINK row, the enforced edge. A document may take every road; an
// app-uploaded photo only another site (an asset is its classification, never
// a move); a field capture never moves; the whole version family goes with the
// current one. Modal + the form shell; every open re-seeds the destination from
// the file as it is then. A move is an ACT — it carries no stamp (the server
// judges the file and its links under the lock).

import { useEffect } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { Modal } from '../ui/Modal.js';
import { displayName, shortFileName, natureOf, type LibraryFile } from './filesDisplay.js';
import { FileDestinationPicker, emptyDestination, useDestinationLabel, type DestinationRoad, type FileDestination } from './FileDestinationPicker.js';
import { FormBody, FormFooter, useFormShell } from './forms/index.js';

export type MoveRoad = DestinationRoad;
export interface MoveTarget {
  road: MoveRoad;
  siteId: string | null;
  assetId: string | null;
  folder: string | null;
  /** The destination in the person's words (the container's toast). */
  where: string;
}

const Schema = z.object({ target: z.custom<FileDestination>((v) => typeof v === 'object' && v !== null) });
type Values = z.infer<typeof Schema>;

export interface MoveFileDialogProps {
  open: boolean;
  projectId: string;
  /** The site the file sits on (an asset-born file: its asset's site); null = no site (a model's paper, a stocked unit's) — no asset road. */
  siteId: string | null;
  file: LibraryFile;
  onClose: () => void;
  /** The roads this file may take (the container decides — a capture takes none). */
  roads: MoveRoad[];
  folders: string[];
  siteFolders: string[];
  assetFolders: string[];
  folderLabel: (folder: string) => string;
  onMove: (file: LibraryFile, target: MoveTarget) => void | Promise<void>;
}

export function MoveFileDialog({ open, projectId, siteId, file, onClose, roads, folders, siteFolders, assetFolders, folderLabel, onMove }: MoveFileDialogProps) {
  const { t } = useTranslation('files');
  const document = natureOf(file) === 'document';
  const first = roads[0] ?? 'site';
  // The destination in words, read while the person picks (the toast's words ride the target).
  const whereRef = { current: '' };
  const shell = useFormShell<Values>({
    schema: Schema.superRefine(({ target: v }, ctx) => {
      const toAsset = v.road === 'asset' || (v.road === 'site' && v.underAsset);
      if (v.road === 'site' && !v.siteId) ctx.addIssue({ code: 'custom', path: ['target', 'site'], message: t('move.pickSite') });
      if (toAsset && !v.assetId) ctx.addIssue({ code: 'custom', path: ['target', 'asset'], message: t('move.pickAsset') });
      if (document && !v.folder) ctx.addIssue({ code: 'custom', path: ['target', 'folder'], message: t('move.pickFolder') });
    }) as unknown as z.ZodType<Values, Values>,
    values: () => ({ target: emptyDestination(first, first === 'folder' ? null : file.folder) }),
    onSubmit: async ({ target: v }) => {
      const toAsset = v.road === 'asset' || (v.road === 'site' && v.underAsset);
      await onMove(file, { road: v.road, siteId: v.road === 'site' ? v.siteId : null, assetId: toAsset ? v.assetId : null, folder: document ? v.folder : null, where: whereRef.current });
    },
    onSuccess: onClose,
  });
  // Every open starts from the file as it is now and without the last attempt's line (a passive effect — an RHF reset).
  const { reseed } = shell;
  useEffect(() => {
    if (open) reseed();
  }, [open, reseed]);
  const target = shell.form.watch('target');
  const where = useDestinationLabel(projectId, siteId ?? '', target, { folderLabel, document });
  whereRef.current = where;
  const leaves = target.road !== 'folder';
  const fieldErrors = shell.form.formState.errors.target as unknown as Partial<Record<'site' | 'asset' | 'folder', { message?: string }>> | undefined;

  return (
    <>
      <Modal open={open} onClose={() => shell.requestLeave(onClose)} title={t('move.title')} maxWidth="max-w-md" footer={<FormFooter shell={shell} onCancel={onClose} submitLabel={t('move.submit')} />}>
        <FormBody shell={shell}>
          <Controller
            control={shell.form.control}
            name="target"
            render={({ field }) => (
              <FileDestinationPicker
                projectId={projectId}
                siteId={siteId ?? ''}
                folderClass={file.anchor.kind === 'asset' ? 'asset' : 'site'}
                roads={siteId ? roads : roads.filter((r) => r !== 'asset')}
                value={field.value}
                onChange={field.onChange}
                document={document}
                currentFolder={file.folder}
                folders={folders}
                siteFolders={siteFolders}
                assetFolders={assetFolders}
                folderLabel={folderLabel}
                errors={{ site: fieldErrors?.site?.message, asset: fieldErrors?.asset?.message, folder: fieldErrors?.folder?.message }}
              />
            )}
          />
          {where && (
            <p className="rounded-md bg-[var(--color-bg-surface-alt)] px-3 py-2 text-sm text-[var(--color-text-secondary)]">
              <span className="font-medium text-[var(--color-text-primary)]">{shortFileName(displayName(file))}</span> → <span className="font-medium text-[var(--color-text-primary)]">{where}</span>
              {leaves && <span className="block text-xs">{t(document && file.older_versions > 0 ? 'move.leavesWithVersions' : 'move.leaves')}</span>}
            </p>
          )}
        </FormBody>
      </Modal>
      {shell.discardDialog}
    </>
  );
}
